import type { CompareCell, CompareGroupId, CompareRow, CompareRowId, CompetitorPlan } from './types'

// Our side of every comparison table. Values mirror PLAN_FEATURES / PLAN_LIMITS
// (app/composables/usePlanConfig.ts), PRICES_EUR (app/composables/useCurrency.ts)
// and the /pricing feature table. Keep in sync when plans, limits, or prices change.
// Honest rows where we are weaker (any URL on Free, store, email capture, ...) stay in.

export const COMPARE_GROUPS: { id: CompareGroupId, label: string }[] = [
  { id: 'pricing', label: 'Pricing' },
  { id: 'editor', label: 'Editor' },
  { id: 'design', label: 'Design' },
  { id: 'content', label: 'Content' },
  { id: 'integrations', label: 'Integrations' },
  { id: 'analytics', label: 'Analytics' },
  { id: 'selling', label: 'Selling and growth' }
]

export const COMPARE_ROWS: CompareRow[] = [
  // Pricing
  { id: 'free_plan', group: 'pricing', label: 'Free plan', mychar: { yes: true, text: 'Free forever, full live editor included' } },
  { id: 'paid_plans', group: 'pricing', label: 'Paid plans', mychar: 'Pro €6.99, Elite €12.99' },
  { id: 'billing', group: 'pricing', label: 'How you pay', mychar: 'Once. No subscription' },
  { id: 'first_year', group: 'pricing', label: 'Main paid plan, first 12 months', mychar: '€6.99 in total (Pro)' },
  { id: 'custom_domain', group: 'pricing', label: 'Custom domain', mychar: { yes: true, text: '€2.99/month add-on, any plan' } },
  { id: 'remove_branding', group: 'pricing', label: 'Remove branding', mychar: { yes: true, text: 'Pro and Elite' } },

  // Editor
  { id: 'editor', group: 'editor', label: 'How you edit', mychar: 'On the page itself, live' },
  { id: 'drag_drop', group: 'editor', label: 'Drag and drop', mychar: { yes: true, text: 'Sections, links, and widgets' } },
  { id: 'themes', group: 'editor', label: 'Ready-made themes', mychar: { yes: true, text: 'Community skins' } },

  // Design
  { id: 'layouts', group: 'design', label: 'Page layouts', mychar: '4 (2 on Free)' },
  { id: 'fonts', group: 'design', label: 'Fonts', mychar: '8 built in (3 on Free)' },
  { id: 'custom_fonts', group: 'design', label: 'Upload your own font', mychar: { yes: true, text: 'Elite, up to 5' } },
  { id: 'effects', group: 'design', label: 'Animated background effects', mychar: { yes: true, text: '7 on Pro, 13 on Elite' } },
  { id: 'cursors', group: 'design', label: 'Custom cursors and cursor trails', mychar: { yes: true, text: 'Pro and Elite' } },
  { id: 'video_background', group: 'design', label: 'Video background', mychar: { yes: true, text: 'Pro and Elite' } },
  { id: 'custom_code', group: 'design', label: 'Custom CSS or code', mychar: false },

  // Content
  { id: 'links', group: 'content', label: 'Links on the free plan', mychar: { yes: true, text: 'Unlimited platform links' } },
  { id: 'any_url', group: 'content', label: 'Links to any URL', mychar: { yes: true, text: '3 on Pro, unlimited on Elite' } },
  { id: 'music_player', group: 'content', label: 'Background music player', mychar: { yes: true, text: 'Every plan' } },
  { id: 'video_clips', group: 'content', label: 'Video clips that play on the page', mychar: { yes: true, text: '6 on Pro, 18 on Elite' } },
  { id: 'comments', group: 'content', label: 'Visitor comments', mychar: { yes: true, text: 'Pro and Elite' } },
  { id: 'view_counter', group: 'content', label: 'View counter on the page', mychar: true },
  { id: 'badges', group: 'content', label: 'Profile badges', mychar: { yes: true, text: 'Achievements' } },
  { id: 'game_library', group: 'content', label: 'Game library with handles', mychar: true },
  { id: 'setup_section', group: 'content', label: 'Gear and setup section', mychar: true },
  { id: 'qr_code', group: 'content', label: 'QR code for your page', mychar: true },

  // Integrations
  { id: 'youtube', group: 'integrations', label: 'YouTube channel and latest videos', mychar: { yes: true, text: 'Pro and Elite' } },
  { id: 'twitch', group: 'integrations', label: 'Twitch live status and schedule', mychar: { yes: true, text: 'Pro and Elite' } },
  { id: 'discord', group: 'integrations', label: 'Discord', mychar: { yes: true, text: 'Profile on Free, live server card on Elite' } },
  { id: 'music_embeds', group: 'integrations', label: 'Spotify or SoundCloud embeds', mychar: { yes: false, text: 'Links only' } },

  // Analytics
  { id: 'analytics_free', group: 'analytics', label: 'Analytics on the free plan', mychar: { yes: true, text: 'Views, clicks, top links' } },
  { id: 'analytics_history', group: 'analytics', label: 'Analytics history', mychar: '7 days, 30 days on Pro, lifetime on Elite' },
  { id: 'analytics_live', group: 'analytics', label: 'Live visitors, countries, and referrers', mychar: { yes: true, text: 'Pro and Elite' } },

  // Selling and growth
  { id: 'store', group: 'selling', label: 'Sell products or courses', mychar: false },
  { id: 'tips', group: 'selling', label: 'Tips and payments', mychar: 'Links to PayPal, Ko-fi, Patreon' },
  { id: 'email_capture', group: 'selling', label: 'Email or SMS signup forms', mychar: false },
  { id: 'scheduling', group: 'selling', label: 'Schedule links', mychar: false },
  { id: 'mobile_app', group: 'selling', label: 'Mobile app', mychar: { yes: false, text: 'Works in any browser' } }
]

/** Our plans for the side-by-side pricing block (EUR, one-time payment mode). */
export const MYCHAR_PLANS: CompetitorPlan[] = [
  { name: 'Free', price: '€0', period: 'forever', note: 'Unlimited platform links, music player, game library, Discord, and analytics.' },
  { name: 'Pro', price: '€6.99', period: 'one-time', note: 'Effects, cursor trails, video backgrounds and clips, custom links, YouTube and Twitch, comments, no branding.' },
  { name: 'Elite', price: '€12.99', period: 'one-time', note: 'Every effect, custom font uploads, unlimited custom links, lifetime analytics, and the highest limits.' },
  { name: 'Custom domain', price: '€2.99', period: '/month', note: 'Optional add-on for any plan, including Free.' }
]

export const COMPARE_ROW_MAP = Object.fromEntries(COMPARE_ROWS.map(row => [row.id, row])) as Record<CompareRowId, CompareRow>

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']

/** "2026-10-01" -> "October 2026" (no Intl, so SSR and client always agree) */
export function formatCheckedMonth(isoDate: string): string {
  const [year, month] = isoDate.split('-')
  return `${MONTHS[Number(month) - 1] ?? ''} ${year}`.trim()
}

/** Plain-text rendering of a cell, used by llms-full.txt and as an accessible label */
export function cellToText(cell: CompareCell | undefined): string {
  if (cell === undefined) return 'Not listed'
  if (typeof cell === 'boolean') return cell ? 'Yes' : 'No'
  if (typeof cell === 'string') return cell
  return `${cell.yes ? 'Yes' : 'No'}: ${cell.text}`
}
